/* Cosa Nostraa — Shop By drawer: group collapse, Show more, promo carousel.
 *
 * ── Why this file exists at all ─────────────────────────────────────────────
 * Dawn's MenuDrawer (global.js) does this when the drawer is constructed:
 *
 *   this.querySelectorAll('button:not(.localization-selector)...')
 *     .forEach(b => b.addEventListener('click', this.onCloseButtonClick))
 *
 * and onCloseButtonClick does `event.currentTarget.closest('details')` then
 * closes it. The Shop By group headings and Show more buttons live inside the
 * Shop By <details>, so without interception the very first tap on "Shop By
 * Categories" would close the entire panel and throw the customer back to the
 * top level of the drawer.
 *
 * A capture-phase listener on document runs BEFORE the event reaches the
 * button, so stopPropagation() there prevents MenuDrawer's handler from ever
 * seeing the click. The buttons then behave as plain buttons.
 *
 * Progressive enhancement: truncation and the collapsed state are rendered
 * server-side (the `hidden` attribute and aria-expanded="true" come from
 * Liquid), so with JS disabled every link is still in the DOM and the panel is
 * simply fully expanded. This file only adds the interaction back.
 */
(function () {
  'use strict';

  if (window.CNShopByLoaded) return;
  window.CNShopByLoaded = true;

  /* ------------------------------------------------------------ group state */

  function eachGroup(fn) {
    var groups = document.querySelectorAll('[data-cn-group]');
    for (var i = 0; i < groups.length; i++) fn(groups[i]);
  }

  /* Apply the Show more state to one group's list.
   * `open` = reveal everything; otherwise show only the first `limit` items.
   * The hidden flags live here rather than in CSS because the threshold is a
   * data attribute, which CSS cannot count. */
  function paintList(group, open) {
    var list = group.querySelector('.cn-shopby__list');
    if (!list) return;
    var limit = parseInt(list.getAttribute('data-cn-limit'), 10);
    if (isNaN(limit)) limit = 0;
    var items = list.children;
    for (var i = 0; i < items.length; i++) {
      items[i].hidden = !open && i >= limit;
    }
  }

  function toggleGroup(group) {
    var btn = group.querySelector('.cn-shopby__toggle');
    if (!btn) return;
    var expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');

    /* Collapsing hides the Show more button via CSS, but its own state has to
     * reset too — otherwise reopening the group lands with everything already
     * expanded and a "Show less" button that no longer matches the list. */
    if (expanded) {
      var more = group.querySelector('.cn-shopby__more');
      if (more) {
        more.setAttribute('aria-expanded', 'false');
        var label = more.querySelector('.cn-shopby__more-label');
        var count = more.getAttribute('data-cn-more-count');
        if (label && count) label.textContent = 'Show ' + count + ' more';
      }
      paintList(group, false);
    }
  }

  function toggleMore(group) {
    var btn = group.querySelector('.cn-shopby__more');
    if (!btn) return;
    var expanded = btn.getAttribute('aria-expanded') === 'true';
    btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    var label = btn.querySelector('.cn-shopby__more-label');
    var count = btn.getAttribute('data-cn-more-count');
    if (label && count) {
      label.textContent = expanded ? 'Show ' + count + ' more' : 'Show less';
    }
    paintList(group, !expanded);
  }

  /* ------------------------------------------------------- promo carousel
   *
   * A CSS scroll-snap carousel, not Swiper. Swiper IS loaded on this theme, on
   * every page, but it would mean an init call, an instance per drawer, and JS
   * work for a two-item strip. Native scroll-snap is handled by the compositor
   * with the platform's own momentum and rubber-banding, which is what a thumb
   * expects, and it needs no JavaScript to swipe at all.
   *
   * This file only adds the dots, because a dot row has to reflect scroll
   * position and that genuinely does need JS. With JS off there is no dot row,
   * which is right: a still strip is not a broken carousel.
   */
  function initCarousel() {
    var wraps = document.querySelectorAll('[data-cn-promos]');
    for (var i = 0; i < wraps.length; i++) {
      var wrap = wraps[i];
      if (wrap.getAttribute('data-cn-promos-ready') === '1') continue;

      var track = wrap.querySelector('.cn-drawer__promos-track');
      if (!track) continue;
      var slides = track.children;
      var n = slides.length;

      /* One banner is not a carousel. No dots, no scroll behaviour — just a
       * card, which is what a single item should have been all along. */
      if (n < 2) {
        wrap.setAttribute('data-cn-promos-ready', '1');
        continue;
      }

      var dots = wrap.querySelector('.cn-drawer__promos-dots');
      if (!dots) continue;

      var btns = [];
      for (var d = 0; d < n; d++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'cn-drawer__promos-dot';
        dot.setAttribute('aria-label', 'Go to banner ' + (d + 1) + ' of ' + n);
        dot.setAttribute('data-cn-dot', String(d));
        dots.appendChild(dot);
        btns.push(dot);
      }

      var current = -1;
      function mark(idx) {
        if (idx === current) return;
        current = idx;
        for (var k = 0; k < btns.length; k++) {
          btns[k].setAttribute('aria-current', k === idx ? 'true' : 'false');
        }
      }

      /* Which slide are we on? Measured from scrollLeft against the track's
       * own width rather than with IntersectionObserver, because a partially
       * visible neighbour would otherwise flicker the dot as it peeks in. */
      function sync() {
        var step = track.scrollWidth / n;
        if (step <= 0) return;
        var idx = Math.round(track.scrollLeft / step);
        if (idx < 0) idx = 0;
        if (idx > n - 1) idx = n - 1;
        mark(idx);
      }

      var ticking = false;
      track.addEventListener(
        'scroll',
        function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(function () {
            ticking = false;
            sync();
          });
        },
        { passive: true }
      );

      /* Dot taps are intercepted in the capture phase for the same reason the
       * group buttons are: MenuDrawer closes on any button inside a <details>. */
      dots.addEventListener(
        'click',
        function (e) {
          var t = e.target;
          if (!t || typeof t.closest !== 'function') return;
          var dot = t.closest('[data-cn-dot]');
          if (!dot) return;
          e.preventDefault();
          e.stopPropagation();
          var idx = parseInt(dot.getAttribute('data-cn-dot'), 10);
          var step = track.scrollWidth / n;
          track.scrollTo({ left: step * idx, behavior: 'smooth' });
        },
        true
      );

      mark(0);
      wrap.setAttribute('data-cn-promos-ready', '1');
    }
  }

  /* ------------------------------------------------------------- listeners */

  /* Capture phase — see the note at the top of the file. stopPropagation()
   * here stops the event before it reaches the button's own listeners. */
  function onClick(e) {
    var t = e.target;
    if (!t || typeof t.closest !== 'function') return;

    var more = t.closest('.cn-shopby__more');
    if (more) {
      e.preventDefault();
      e.stopPropagation();
      toggleMore(more.closest('[data-cn-group]'));
      return;
    }

    var toggle = t.closest('.cn-shopby__toggle');
    if (toggle) {
      e.preventDefault();
      e.stopPropagation();
      toggleGroup(toggle.closest('[data-cn-group]'));
    }
  }

  /* Enter and Space already fire click on a <button>. This is here for the
   * case where focus sits on the chevron span and a key bubbles up from a
   * child, and as belt-and-braces for older mobile WebKit. */
  function onKeydown(e) {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    var t = e.target;
    if (!t || typeof t.closest !== 'function') return;
    if (!t.closest('.cn-shopby__more') && !t.closest('.cn-shopby__toggle')) return;
    /* Let the browser synthesise the click; only block MenuDrawer. */
    e.stopPropagation();
  }

  /* ------------------------------------------------------------------ init */

  function init() {
    if (!document.documentElement.classList.contains('cn-shopby-ready')) {
      eachGroup(function (group) {
        paintList(group, false);
      });
      document.documentElement.classList.add('cn-shopby-ready');
    }
    initCarousel();
  }

  document.addEventListener('click', onClick, true);
  document.addEventListener('keydown', onKeydown, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Some theme sections re-render their markup; re-apply to anything new. */
  document.addEventListener('shopify:section:load', init);
})();